<template>
  <div class="field-list">
    <template v-if="isArray">
      <div class="list-values">
        <small
          v-for="(val, index) in value"
          :key="`${getValueId(val) || index}`"
          class="badge badge-primary m-1 float-left clickable-badge cursor-pointer"
          :style="badgeStyleFor(val)"
          @click.stop="navigateToFiltered(val)"
          :title="$t('Click to filter by this value')"
        >
          <span v-if="val">{{ getHierarchicalLabel(val) }}</span>
        </small>
      </div>
    </template>
    <template v-else>
      <span
        class="badge badge-primary clickable-badge cursor-pointer"
        v-if="value"
        :style="badgeStyleFor(value)"
        @click.stop="navigateToFiltered(value)"
        :title="$t('Click to filter by this value')"
      >{{ getHierarchicalLabel(value) }}</span>
    </template>
  </div>
</template>

<script>
import { getFieldListItems } from "@/utils/fieldListItems";
import { getListValueBadgeStyle } from "@/utils/listItemColor";

export default {
  name: "FieldList",
  props: {
    value: {
      type: [Object, Array],
    },
    isArray: {
      type: Boolean,
      default: false,
    },
    field: {
      type: Object,
      default: () => ({}),
    },
    referenceItems: {
      type: [Array, Object],
      default: () => ({}),
    },
    lists: {
      type: Array,
      default: () => [],
    },
    modelName: {
      type: String,
      default: "",
    },
  },
  methods: {
    /**
     * Fondo del badge con el color del ítem de la lista, o `null` (badge por defecto)
     * si ese ítem no tiene color asignado en el editor de listas.
     */
    badgeStyleFor(val) {
      return getListValueBadgeStyle(val, this.getListOptions());
    },
    getValueId(val) {
      if (!val) return null;
      if (typeof val === 'object') return val._id || val.id || val.label;
      return val;
    },
    navigateToFiltered(val) {
      const valId = this.getValueId(val);
      if (!valId || !this.modelName || !this.field?.key) return;
      
      const url = `/dynamic-model/${this.modelName}?${this.field.key}=${valId}`;
      this.$router.push(url);
    },
    getHierarchicalLabel(value) {
      if (!value) return "";
      
      // Si el valor es un string que parece JSON, intentar parsearlo
      let parsedValue = value;
      if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
        try {
          parsedValue = JSON.parse(value);
        } catch (e) {
          // Si no es JSON válido, usar el valor original
          parsedValue = value;
        }
      }
      
      // Si el valor tiene hierarchy (viene del backend), usarlo directamente
      if (typeof parsedValue === 'object' && parsedValue !== null && parsedValue.hierarchy) {
        return String(parsedValue.hierarchy);
      }
      
      // Si el valor es un objeto y tiene label, construir jerarquía si es necesario
      if (typeof parsedValue === 'object' && parsedValue !== null && parsedValue !== undefined) {
        // Si tiene label, construir jerarquía completa recursivamente
        if (parsedValue.label !== undefined && parsedValue.label !== null) {
          const listOptions = this.getListOptions();
          if (listOptions && listOptions.length > 0) {
            // Función recursiva para construir la jerarquía completa
            const buildHierarchy = (itemId, visited = new Set()) => {
              if (!itemId || visited.has(String(itemId))) {
                return [];
              }
              visited.add(String(itemId));
              
              const item = listOptions.find((opt) => {
                const optId = opt._id || opt.id;
                return optId && optId.toString() === String(itemId);
              });
              
              if (!item || !item.label) {
                return [];
              }
              
              const hierarchy = [String(item.label)];
              
              // Si tiene parent, construir la jerarquía del padre primero
              if (item.parent) {
                const parentHierarchy = buildHierarchy(item.parent, visited);
                return [...parentHierarchy, ...hierarchy];
              }
              
              return hierarchy;
            };
            
            // Obtener el ID del valor
            const valueId = parsedValue._id || parsedValue.id;
            if (valueId) {
              const hierarchy = buildHierarchy(valueId);
              if (hierarchy.length > 1) {
                return hierarchy.join(" > ");
              }
              // Si solo hay un nivel, retornar solo el label
              return String(parsedValue.label);
            }
          }
          
          // Si no hay opciones de lista o no se puede construir jerarquía, retornar solo el label
          return String(parsedValue.label);
        }
        
        // Si no tiene label pero tiene _id, buscar en las opciones de lista
        const valueId = parsedValue._id || parsedValue.id;
        if (valueId) {
          const listOptions = this.getListOptions();
          if (listOptions && listOptions.length > 0) {
            // Función recursiva para construir la jerarquía completa
            const buildHierarchy = (itemId, visited = new Set()) => {
              if (!itemId || visited.has(String(itemId))) {
                return [];
              }
              visited.add(String(itemId));
              
              const item = listOptions.find((opt) => {
                const optId = opt._id || opt.id;
                return optId && optId.toString() === String(itemId);
              });
              
              if (!item || !item.label) {
                return [];
              }
              
              const hierarchy = [String(item.label)];
              
              // Si tiene parent, construir la jerarquía del padre primero
              if (item.parent) {
                const parentHierarchy = buildHierarchy(item.parent, visited);
                return [...parentHierarchy, ...hierarchy];
              }
              
              return hierarchy;
            };
            
            const hierarchy = buildHierarchy(valueId);
            if (hierarchy.length > 0) {
              return hierarchy.join(" > ");
            }
          }
        }
      }
      
      // Si el valor es un string o número (ID), buscar en las opciones de lista
      const valueId = typeof parsedValue === 'object' ? (parsedValue._id || parsedValue.id) : parsedValue;
      if (valueId) {
        const listOptions = this.getListOptions();
        if (listOptions && listOptions.length > 0) {
          // Función recursiva para construir la jerarquía completa
          const buildHierarchy = (itemId, visited = new Set()) => {
            if (!itemId || visited.has(String(itemId))) {
              return [];
            }
            visited.add(String(itemId));
            
            const item = listOptions.find((opt) => {
              const optId = opt._id || opt.id;
              return optId && optId.toString() === String(itemId);
            });
            
            if (!item || !item.label) {
              return [];
            }
            
            const hierarchy = [String(item.label)];
            
            // Si tiene parent, construir la jerarquía del padre primero
            if (item.parent) {
              const parentHierarchy = buildHierarchy(item.parent, visited);
              return [...parentHierarchy, ...hierarchy];
            }
            
            return hierarchy;
          };
          
          const hierarchy = buildHierarchy(valueId);
          if (hierarchy.length > 0) {
            return hierarchy.join(" > ");
          }
        }
      }
      
      // Fallback: si es un objeto, intentar mostrar su representación de string
      if (typeof parsedValue === 'object' && parsedValue !== null) {
        return String(parsedValue);
      }
      
      return String(parsedValue || "");
    },

    getListOptions() {
      return getFieldListItems(this.field, {
        lists: this.lists,
        referenceItems: this.referenceItems,
      });
    },
  },
};
</script>

<style scoped>
.field-list .badge {
  font-size: 0.8em;
  padding: 4px 8px;
}
</style>
